/**
 * Modal for adding a new Max messenger account via QR authentication.
 *
 * Flow:
 *   1. On mount — вызываем startMaxAuth(), получаем qrLink и sessionId
 *   2. Показываем QR код, пользователь сканирует его в мобильном Max
 *   3. Каждые 2 секунды polling pollMaxAuthStatus(sessionId)
 *   4. Когда статус 'authenticated' — показываем поле "Имя аккаунта"
 *   5. По кнопке "Готово" → completeMaxAuth(sessionId, accountName)
 *   6. Закрываем модалку и вызываем onSuccess
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import toast from 'react-hot-toast';
import {
	startMaxAuth,
	pollMaxAuthStatus,
	completeMaxAuth,
	submitMaxAuthPassword,
} from '../../services/messengerService';

// Цветовая схема проекта (повторяет IntegrationsContent)
const colors = {
	primary: '#6366F1',
	primaryHover: '#4F46E5',
};

type AuthStep = 'loading' | 'scan' | 'naming' | 'submitting' | 'error' | 'password';

interface MaxAuthDialogProps {
	isOpen: boolean;
	onClose: () => void;
	onSuccess: () => void;
}

const POLL_INTERVAL_MS = 2000;

const MaxAuthDialog: React.FC<MaxAuthDialogProps> = ({ isOpen, onClose, onSuccess }) => {
	const [step, setStep] = useState<AuthStep>('loading');
	const [sessionId, setSessionId] = useState<string | null>(null);
	const [qrLink, setQrLink] = useState<string | null>(null);
	const [expiresAt, setExpiresAt] = useState<number | null>(null);
	const [accountName, setAccountName] = useState('');
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	const [passwordHint, setPasswordHint] = useState<string | null>(null);
	const [passwordEmail, setPasswordEmail] = useState<string | null>(null);
	const [passwordValue, setPasswordValue] = useState('');
	const [submittingText, setSubmittingText] = useState('Создаём аккаунт и запускаем клиент...');

	// Храним интервал polling'а в ref, чтобы надёжно чистить в cleanup
	const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

	const stopPolling = useCallback(() => {
		if (pollIntervalRef.current) {
			clearInterval(pollIntervalRef.current);
			pollIntervalRef.current = null;
		}
	}, []);

	// Старт авторизации при открытии модалки
	useEffect(() => {
		if (!isOpen) return;

		let cancelled = false;
		setStep('loading');
		setErrorMessage(null);
		setAccountName('');
		setPasswordValue('');
		setPasswordHint(null);
		setPasswordEmail(null);

		(async () => {
			try {
				const resp = await startMaxAuth();
				if (cancelled) return;
				setSessionId(resp.sessionId);
				setQrLink(resp.qrLink);
				setExpiresAt(resp.expiresAt);
				setStep('scan');
			} catch (err: any) {
				if (cancelled) return;
				setStep('error');
				setErrorMessage(err?.response?.data?.error || err?.message || 'Не удалось подключиться к MAX. Попробуйте ещё раз.');
			}
		})();

		return () => {
			cancelled = true;
			stopPolling();
		};
	}, [isOpen, stopPolling]);

	// Polling статуса во время шага 'scan'
	useEffect(() => {
		if (step !== 'scan' || !sessionId) return;

		const poll = async () => {
			try {
				const resp = await pollMaxAuthStatus(sessionId);
				if (resp.status === 'authenticated') {
					stopPolling();
					setStep('naming');
					return;
				}
				if (resp.status === 'password_required') {
					stopPolling();
					setPasswordHint(resp.hint || null);
					setPasswordEmail(resp.email || null);
					setStep('password');
					return;
				}
				if (resp.status === 'expired') {
					stopPolling();
					setStep('error');
					setErrorMessage('QR-код истёк. Закройте окно и попробуйте снова.');
					return;
				}
				if (resp.status === 'not_found') {
					stopPolling();
					setStep('error');
					setErrorMessage('Сессия авторизации не найдена. Закройте окно и начните заново.');
					return;
				}
				if (resp.status === 'error') {
					stopPolling();
					setStep('error');
					setErrorMessage(resp.message || 'Не удалось подключить MAX-аккаунт. Попробуйте ещё раз или обратитесь в поддержку.');
					return;
				}
				// status === 'pending' — продолжаем опрос
			} catch (err: any) {
				// Одна неудачная попытка не ломает polling, только логируем.
				// Если сеть отвалилась — следующий тик получит актуальное состояние.
				console.warn('pollMaxAuthStatus failed:', err?.message);
			}
		};

		// Сразу первый опрос, потом интервал
		poll();
		pollIntervalRef.current = setInterval(poll, POLL_INTERVAL_MS);

		return () => stopPolling();
	}, [step, sessionId, stopPolling]);

	// Таймер оставшегося времени — визуализация когда QR/сессия истекает
	const [secondsLeft, setSecondsLeft] = useState<number | null>(null);
	useEffect(() => {
		if ((step !== 'scan' && step !== 'password') || !expiresAt) {
			setSecondsLeft(null);
			return;
		}
		const tick = () => {
			const left = Math.max(0, Math.floor((expiresAt - Date.now()) / 1000));
			setSecondsLeft(left);
			if (left <= 0) {
				stopPolling();
				setStep('error');
				setErrorMessage('QR-код истёк. Закройте окно и попробуйте снова.');
			}
		};
		tick();
		const timerId = setInterval(tick, 1000);
		return () => clearInterval(timerId);
	}, [step, expiresAt, stopPolling]);

	const handlePasswordSubmit = async () => {
		if (!sessionId || !passwordValue.trim()) return;
		setSubmittingText('Проверяем пароль...');
		setStep('submitting');
		try {
			const resp = await submitMaxAuthPassword(sessionId, passwordValue);
			if (resp.status === 'authenticated') {
				setStep('naming');
			} else {
				setStep('password');
				toast.error(resp.message || 'Неверный пароль. Попробуйте ещё раз.');
			}
		} catch (err: any) {
			setStep('password');
			toast.error(err?.response?.data?.error || err?.message || 'Ошибка при отправке пароля.');
		}
	};

	const handleComplete = async () => {
		if (!sessionId) return;
		const trimmed = accountName.trim();
		if (!trimmed) {
			toast.error('Введите имя аккаунта');
			return;
		}
		setSubmittingText('Создаём аккаунт и запускаем клиент...');
		setStep('submitting');
		try {
			const resp = await completeMaxAuth(sessionId, trimmed);
			toast.success(`Max аккаунт "${resp.accountName}" подключён`);
			onSuccess();
		} catch (err: any) {
			setStep('naming');
			toast.error(err?.response?.data?.error || err?.message || 'Не удалось сохранить MAX-аккаунт. Попробуйте ещё раз.');
		}
	};

	const handleClose = () => {
		stopPolling();
		onClose();
	};

	if (!isOpen) return null;

	return (
		<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
			<div className="bg-white rounded-lg p-6 max-w-md w-full">
				<h3 className="text-lg font-semibold mb-4">Подключение Max аккаунта</h3>

				{step === 'loading' && (
					<div className="text-center py-8 text-gray-600">
						Запрашиваем QR-код...
					</div>
				)}

				{step === 'scan' && qrLink && (
					<div>
						<p className="text-sm text-gray-600 mb-3">
							Отсканируйте QR-код в мобильном приложении Max:
							Настройки → Устройства → Подключить устройство.
						</p>
						<div className="flex justify-center my-4 p-4 bg-white border-2 border-gray-200 rounded-lg">
							<QRCodeSVG value={qrLink} size={240} level="M" includeMargin={false} />
						</div>
						{secondsLeft !== null && (
							<p className="text-xs text-gray-500 text-center mb-3">
								QR-код действителен ещё {secondsLeft} сек
							</p>
						)}
						<div className="flex justify-end">
							<button
								onClick={handleClose}
								className="px-4 py-2 text-gray-600 hover:text-gray-800"
							>
								Отмена
							</button>
						</div>
					</div>
				)}

				{step === 'naming' && (
					<div>
						<p className="text-sm text-green-600 mb-4">
							QR-код успешно отсканирован. Введите имя аккаунта, под которым
							он будет отображаться в интерфейсе.
						</p>
						<input
							type="text"
							placeholder="Например: Олег — HR"
							value={accountName}
							onChange={(e) => setAccountName(e.target.value)}
							className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
							maxLength={100}
							style={{ '--tw-ring-color': colors.primary } as React.CSSProperties}
							autoFocus
						/>
						<div className="flex justify-end mt-4 space-x-2">
							<button
								onClick={handleClose}
								className="px-4 py-2 text-gray-600 hover:text-gray-800"
							>
								Отмена
							</button>
							<button
								onClick={handleComplete}
								disabled={!accountName.trim()}
								className="px-4 py-2 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
								style={{ backgroundColor: colors.primary }}
								onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = colors.primaryHover)}
								onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = colors.primary)}
							>
								Готово
							</button>
						</div>
					</div>
				)}

				{step === 'password' && (
					<div>
						<p className="text-sm text-gray-600 mb-2">
							Аккаунт защищён облачным паролем.
						</p>
						{passwordEmail && (
							<p className="text-xs text-gray-500 mb-1">Аккаунт: {passwordEmail}</p>
						)}
						{passwordHint && (
							<p className="text-xs text-gray-500 mb-1">Подсказка: {passwordHint}</p>
						)}
						{secondsLeft !== null && secondsLeft <= 60 && (
							<p className="text-xs text-amber-500 mb-3">
								Сессия истекает через {secondsLeft} сек
							</p>
						)}
						<input
							type="password"
							placeholder="Введите облачный пароль"
							value={passwordValue}
							onChange={(e) => setPasswordValue(e.target.value)}
							onKeyDown={(e) => e.key === 'Enter' && handlePasswordSubmit()}
							className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
							maxLength={64}
							autoFocus
						/>
						<div className="flex justify-end mt-4 space-x-2">
							<button onClick={handleClose} className="px-4 py-2 text-gray-600 hover:text-gray-800">
								Отмена
							</button>
							<button
								onClick={handlePasswordSubmit}
								disabled={!passwordValue.trim()}
								className="px-4 py-2 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
								style={{ backgroundColor: colors.primary }}
							>
								Войти
							</button>
						</div>
					</div>
				)}

				{step === 'submitting' && (
					<div className="text-center py-8 text-gray-600">
						{submittingText}
					</div>
				)}

				{step === 'error' && (
					<div>
						<p className="text-sm text-red-600 mb-4">
							{errorMessage || 'Неизвестная ошибка'}
						</p>
						<div className="flex justify-end">
							<button
								onClick={handleClose}
								className="px-4 py-2 text-white rounded-lg"
								style={{ backgroundColor: colors.primary }}
							>
								Закрыть
							</button>
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

export default MaxAuthDialog;
